/**
 * Product components. Loaded once, deferred, on every page, because product
 * cards (quick-add) and rails appear almost everywhere.
 *
 *   <variant-picker>           native colour and size picker
 *   <product-form>             "Add to bag" form
 *   <sticky-buy>               bar on phones, shown while the real button is off screen
 *   <product-gallery>          swipeable product photos with progress lines; shows only the
 *                              chosen colour's photos when the product has them grouped, and
 *                              opens a photo full screen (assets/product-viewer.js)
 *   <product-details>          the details column, kept in view beside the photos on a wide screen
 *   <kanze-rail>               horizontal row of cards with arrows
 *   <product-recommendations>  loads suggested products when scrolled near
 *   [data-quick-add]           opens the size sheet for a product
 *
 * A "product root" is the element with [data-product-root] that wraps one
 * product's picker, price, stock and form. After a variant change, every
 * [data-refresh="…"] region inside the root is replaced with fresh HTML from
 * Shopify, and the root fires `variant:change`.
 */
const Kanze = (window.Kanze = window.Kanze || {});

/**
 * Replaces each [data-refresh] region inside `root` with the region of the same
 * name found in `html`. Keeps keyboard focus on the same control.
 * @param {Element} root
 * @param {string} html
 * @param {string[]} [keep] - Names of regions to leave exactly as they are
 */
Kanze.refresh = (root, html, keep = []) => {
  const fresh = new DOMParser().parseFromString(html, 'text/html');
  const focusedId = root.contains(document.activeElement) ? document.activeElement.id : '';
  root.querySelectorAll('[data-refresh]').forEach((region) => {
    if (keep.includes(region.dataset.refresh)) return;
    const replacement = fresh.querySelector(`[data-refresh="${region.dataset.refresh}"]`);
    if (replacement) region.innerHTML = replacement.innerHTML;
  });
  if (focusedId) Kanze.focus(document.getElementById(focusedId));
};

/** The handle of the product a part of the page belongs to, read from its address. */
const handleIn = (root) => decodeURIComponent((root?.querySelector('product-form, variant-picker')?.dataset.url || '').match(/\/products\/([^/?#]+)/)?.[1] || '');

/** Says what happened, to the theme's tracking (assets/kz-track.js, docs/design/WEB-TRACKING-PLAN.md). */
const track = (name, props, how) => window.kz?.track?.(name, props, how);

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => this.onChange(event));
    this.addEventListener('click', (event) => this.onRefused(event));
  }

  /**
   * A sold-out size was tapped. It cannot be chosen; the end of the size line says so
   * ("M is sold out"). That line has its own place, so nothing moves.
   */
  onRefused(event) {
    const chip = event.target instanceof Element ? event.target.closest('label.picker__button.is-unavailable') : null;
    if (!chip?.control?.disabled) return;
    const row = chip.closest('[data-size-option]');
    if (row) track('OOS Size Tapped', { product_handle: handleIn(this.root), size: chip.control.value, variant_id: Number(chip.control.dataset.variantId) || undefined });
    const status = row?.querySelector('[data-size-status]');
    if (!status) return;
    status.classList.remove('is-asking');
    status.textContent = (row.dataset.soldOutText || '').replace('[size]', chip.control.value);
  }

  get root() {
    return this.closest('[data-product-root]');
  }

  /**
   * Keeps the address in step with the chosen variant, so Back, a reload and a restored tab
   * come back to it. The address is written when he leaves the page, never at the tap: a
   * browser can answer a change of address by opening its own bars again (iPhone Safari with
   * the address bar at the top, and the browsers inside apps, do), and the whole page then
   * moves down under his finger.
   * @param {string} variantId
   */
  static remember(variantId) {
    VariantPicker.wanted = String(variantId);
    if (VariantPicker.keeping) return;
    VariantPicker.keeping = true;
    const write = () => {
      const page = new URL(window.location.href);
      if (!VariantPicker.wanted || page.searchParams.get('variant') === VariantPicker.wanted) return;
      page.searchParams.set('variant', VariantPicker.wanted);
      window.history.replaceState(window.history.state, '', page);
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') write();
    });
    window.addEventListener('pagehide', write);
  }

  /** The option value ids that describe the wanted variant, in option order. */
  optionValueIds() {
    return [...this.querySelectorAll('[data-option], input[data-size-in-sheet]')]
      .map((part) => {
        if (part.matches('input')) return part.dataset.optionValueId;
        const chosen = part.querySelector('input:checked') || part.querySelector('input[data-selected]');
        return chosen ? chosen.dataset.optionValueId : null;
      })
      .filter(Boolean);
  }

  async onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const root = this.root;
    const requireSize = this.hasAttribute('data-require-size');
    const updateUrl = this.hasAttribute('data-update-url');
    if (requireSize && input.closest('[data-size-option]')) root.dataset.sizeChosen = 'true';
    // How this size came to be chosen (the tracking plan's `size_source`): by him, unless the
    // script that chose it says otherwise (Find my size, or a size named in the address).
    if (input.closest('[data-size-option]') && root) root.dataset.sizeBy = event.isTrusted ? 'manual' : input.dataset.by || 'manual';
    Kanze.sayAtSizes(root, '');

    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('option_values', this.optionValueIds().join(','));
    url.searchParams.set('section_id', this.dataset.section);

    // What is sold out in an option depends only on the options before it. So a choice in the
    // last option (a size, on every product today) changes nothing in the picker but the choice
    // itself: the picker is left exactly as it is, and nothing around the sizes can move.
    const group = input.closest('[data-option]');
    const parts = [...this.querySelectorAll('[data-option], input[data-size-in-sheet]')];
    const still = Boolean(group) && parts[parts.length - 1] === group;
    if (still) {
      for (const other of group.querySelectorAll('input[data-selected]')) other.removeAttribute('data-selected');
      input.setAttribute('data-selected', '');
    }

    VariantPicker.pending?.abort();
    const controller = new AbortController();
    VariantPicker.pending = controller;
    root.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Variant request failed (${response.status})`);
      Kanze.refresh(root, await response.text(), still ? ['picker'] : []);
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Kanze: could not load the chosen variant.', error);
      return;
    } finally {
      if (VariantPicker.pending === controller) root.removeAttribute('aria-busy');
    }

    // The picker was re-rendered. In the sheet, sizes come back unchosen, so restore the customer's choice.
    if (requireSize && root.dataset.sizeChosen) {
      const chosenSize = root.querySelector('[data-size-option] input[data-selected]:not(:disabled)');
      if (chosenSize) chosenSize.checked = true;
      else delete root.dataset.sizeChosen;
    }

    const form = root.querySelector('product-form');
    const variantId = form?.dataset.variantId;
    if (updateUrl && variantId) VariantPicker.remember(variantId);

    root.dispatchEvent(
      new CustomEvent('variant:change', { bubbles: true, detail: { variantId, featuredMediaId: form?.dataset.featuredMediaId } })
    );
  }
}

/**
 * Asks for a size when "Add to bag" is pressed with none chosen. One cue, the same wherever a
 * size is asked for: the end of the size line says "Select a size" in the sale colour, and the
 * sizes give one short shake. No box is drawn and nothing changes size or place. The page
 * scrolls only when the sizes are not on screen. With the keyboard, focus goes to the first
 * size. Nothing is added to the bag.
 * @param {Element} root - The product root
 */
Kanze.nudgeSize = (root) => {
  const row = root?.querySelector('variant-picker [data-size-option]');
  if (!row) return;
  Kanze.sayAtSizes(root, root.querySelector('product-form')?.dataset.labelSelect || '');
  Kanze.shake(row);
  if (Kanze.keys) Kanze.focus(row.querySelector('input:not(:disabled)'));
};

/**
 * One short shake of a row of sizes (CSS, transform only; none with "reduce motion"), after
 * bringing it on screen if it is not: under the header, under the phone's buy bar, or away.
 * @param {Element} row - The element that carries `is-nudged`
 */
Kanze.shake = (row) => {
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const box = row.getBoundingClientRect();
  const bar = document.querySelector('sticky-buy.is-shown')?.offsetHeight || 0;
  const inDialog = row.closest('dialog');
  if (!inDialog && (box.top < 0 || box.bottom > window.innerHeight - bar)) row.scrollIntoView({ block: 'center', behavior: calm ? 'auto' : 'smooth' });
  row.classList.remove('is-nudged');
  // Reading a size forces the browser to notice the class left, so the shake can run again.
  void row.offsetWidth;
  row.classList.add('is-nudged');
  window.setTimeout(() => row.classList.remove('is-nudged'), 600);
};

/**
 * Writes a line at the end of the size line, in the sale colour: "Select a size", or why a
 * piece could not be added. The place is always there, so writing in it moves nothing, and
 * screen readers read it out. An empty message clears it.
 * @param {Element} root - The product root
 * @param {string} message
 * @returns {boolean} False when this product has no size line to write on
 */
Kanze.sayAtSizes = (root, message) => {
  const spot = root?.querySelector('variant-picker [data-size-status]');
  if (!spot) return false;
  spot.textContent = message || '';
  spot.classList.toggle('is-asking', Boolean(message));
  return true;
};

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('button[type="submit"]');
    this.label = this.querySelector('[data-label]');
    this.error = this.querySelector('[data-error]');
    this.root = this.closest('[data-product-root]');
    // In the size sheet opened by "Change size" in the bag, the button says so.
    this.addLabel = this.root?.dataset.swapKey ? this.root.dataset.swapLabel : this.dataset.labelAdd || this.label?.textContent;

    this.onSubmit = this.onSubmit.bind(this);
    this.sync = this.sync.bind(this);
    this.form?.addEventListener('submit', this.onSubmit);
    this.root?.addEventListener('variant:change', this.sync);
    this.root?.addEventListener('change', this.sync);
    this.sync();
  }

  disconnectedCallback() {
    this.root?.removeEventListener('variant:change', this.sync);
    this.root?.removeEventListener('change', this.sync);
  }

  /** True while the customer still has to pick a size (the product page and the bottom sheet). */
  get needsSize() {
    const picker = this.root?.querySelector('variant-picker[data-require-size]');
    const sizes = picker?.querySelector('[data-size-option]');
    return Boolean(sizes) && !sizes.querySelector('input:checked');
  }

  sync() {
    if (!this.button || this.button.disabled || !this.label) return;
    const needsSize = this.needsSize;
    this.label.textContent = needsSize ? this.dataset.labelSelect : this.addLabel;
    this.button.setAttribute('aria-disabled', String(needsSize));
  }

  /**
   * Why the piece could not be added. It is written at the end of the size line, a place that
   * is always there, so the button does not move down to make room for it. A product with no
   * sizes has no such line: there the message shows above the button, as before.
   */
  showError(message) {
    const atSizes = Kanze.sayAtSizes(this.root, message);
    if (this.error) {
      this.error.textContent = atSizes ? '' : message || '';
      this.error.hidden = atSizes || !message;
    }
    this.root?.dispatchEvent(new CustomEvent('buy:error', { detail: { message: message || '' } }));
  }

  /**
   * "Adding…" on the button (and on the phone bar's twin) from the tap until Shopify answers.
   * The words are in the button already and show over its own while it is busy (CSS).
   */
  busy(on) {
    if (on) this.button.setAttribute('aria-busy', 'true');
    else this.button.removeAttribute('aria-busy');
    this.root?.querySelector('sticky-buy')?.mirror?.();
  }

  /** What the page already says about the piece, for the confirmation while it waits for Shopify. */
  shown() {
    const root = this.root;
    const picker = root?.querySelector('variant-picker');
    const options = [picker?.querySelector('.picker__selected')?.textContent.trim(), picker?.querySelector('[data-size-option] input:checked')?.value].filter(Boolean);
    return { title: root?.querySelector('.product__title, .quick-add__title')?.textContent.trim() || '', options: options.join(' · '), price: this.button.querySelector('.price')?.textContent.trim() || '' };
  }

  /**
   * The photo of this piece that is on the page, for the flight to the bag: the card the size
   * sheet was opened from, or the photo showing in the product page's gallery.
   */
  photo() {
    const opener = this.closest('#QuickAdd') ? Kanze.quickAddOpener : null;
    const card = opener?.closest('.card');
    if (card) return card.querySelector('.card__image');
    const root = opener?.closest('[data-product-root]') || this.root;
    return root?.querySelector('product-gallery')?.photo || null;
  }

  /** Where this add was made from, in the tracking plan's words (`Add to Cart.source`). */
  source(origin) {
    const sheet = this.closest('#QuickAdd');
    const opener = sheet ? Kanze.quickAddOpener : origin;
    const job = opener?.closest('recs-rail')?.dataset.job;
    if (job) return `recs_${job}`;
    if (opener?.closest('sticky-buy')) return 'sticky_bar';
    // The size sheet opened from the product page's own button (sizes chosen in the sheet).
    const page = sheet ? opener?.closest('[data-product-root]') : null;
    if (page) return page.dataset.addVia || 'product_page';
    return sheet ? 'quick_add' : 'product_page';
  }

  /** How the size being added came to be chosen (`Add to Cart.size_source`). */
  sizeSource() {
    const root = this.root;
    const sizes = root?.querySelectorAll('variant-picker [data-size-option] input[type="radio"]') || [];
    if (sizes.length <= 1) return 'single_size';
    if (root.dataset.sizeBy) return root.dataset.sizeBy;
    return new URLSearchParams(window.location.search).has('variant') && !this.closest('#QuickAdd') ? 'url' : 'manual';
  }

  /** "Change size" in the bag: the new size takes the old one's place, same quantity. */
  async swap(id) {
    const root = this.root;
    const dialog = this.closest('dialog');
    if (id === Number(root.dataset.swapVariant)) {
      dialog?.close();
      return;
    }
    this.button.setAttribute('aria-busy', 'true');
    for (const bag of document.querySelectorAll('cart-items')) bag.moving?.(root.dataset.swapKey);
    const sizes = [...root.querySelectorAll('variant-picker [data-size-option] input[type="radio"]')];
    const sizeOf = (variant) => sizes.find((input) => Number(input.dataset.variantId) === Number(variant))?.value;
    try {
      await Kanze.cart.swap(root.dataset.swapKey, id, Number(root.dataset.swapQuantity) || 1);
      track('Cart Size Changed', { product_handle: handleIn(root), from: sizeOf(root.dataset.swapVariant), to: sizeOf(id) || sizes.find((input) => input.checked)?.value, had_recommendation: Boolean(root.dataset.yourSize) });
      dialog?.close();
    } catch (error) {
      await Kanze.cart.refresh().catch(() => {});
      this.showError(error.message);
    } finally {
      this.button.removeAttribute('aria-busy');
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.button.getAttribute('aria-busy') === 'true') return;
    this.showError('');

    // Size is chosen in the bottom sheet: open it for the current colour instead of adding.
    if (this.root?.querySelector('[data-size-in-sheet]')) {
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('variant', this.dataset.variantId);
      this.root.dataset.addVia = this.root.pressed?.closest('sticky-buy') ? 'sticky_bar' : 'product_page';
      Kanze.quickAdd(`${url.pathname}${url.search}`, this.button);
      return;
    }

    if (this.needsSize) {
      Kanze.nudgeSize(this.root);
      return;
    }

    const data = new FormData(this.form);
    if (this.root?.dataset.swapKey) {
      this.swap(Number(data.get('id')));
      return;
    }

    // The bag does not open. At the tap the button says "Adding…", the photo flies to the bag
    // icon and a quiet confirmation shows (assets/bag-added.js); Shopify's answer confirms
    // them. An add that is refused takes them back and says why, at the sizes.
    const origin = this.root?.pressed || this.button;
    if (this.root) this.root.pressed = null;
    this.busy(true);
    const id = Number(data.get('id'));
    try {
      await Kanze.cart.add([{ id, quantity: Number(data.get('quantity') || 1) }], { from: [this.photo()], origin, shown: [this.shown()], ready: () => this.closest('dialog')?.close(), track: { source: this.source(origin), sizes: { [id]: this.sizeSource() } } });
    } catch (error) {
      this.showError(error.message);
    } finally {
      this.busy(false);
    }
  }
}

/**
 * The buy bar on phones. One rule: it shows whenever the real "Add to bag" button is not
 * on screen (below the fold when the page opens, or scrolled away above). It steps aside when
 * the footer comes into view, while any panel is open, and while it would lie over the
 * product's name and price: on a short screen those sit right under the photo, where the bar
 * is, and he should read them before he is asked to choose a size. Pressing its button presses
 * the real form, so there is one way of adding to the bag.
 */
class StickyBuy extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('[data-product-root]');
    this.target = this.root?.querySelector('[data-refresh="buy"]');
    if (!this.target || !('IntersectionObserver' in window)) return;
    this.realOffScreen = false;
    this.footerOnScreen = false;
    this.update = this.update.bind(this);
    this.sync = this.sync.bind(this);

    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === this.target) this.realOffScreen = !entry.isIntersecting;
        else this.footerOnScreen = entry.isIntersecting;
      }
      this.update();
    });
    this.observer.observe(this.target);
    const footer = document.querySelector('.shopify-section-group-footer-group, footer');
    if (footer) this.observer.observe(footer);

    // Panels (menu, bag, size guide, Find my size, size sheet) all open through Kanze.openDialog.
    document.addEventListener('dialog:open', this.update);
    document.addEventListener('close', this.update, true);
    // The name and price pass under the bar's place as he scrolls: looked at once a frame.
    this.head = this.root.querySelector('.product__head');
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    };
    if (this.head) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      // They can also move without a scroll, while the page is still being laid out or when
      // something above them arrives late (the app bar, a photo): the page's height changes
      // then. The bar takes its new state at once, in the same frame, with no slide: he did
      // nothing, so nothing should be seen moving.
      this.pageObserver = new ResizeObserver(() => {
        this.classList.add('is-instant');
        this.update();
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.classList.remove('is-instant')));
      });
      this.pageObserver.observe(document.body);
    }
    this.root.addEventListener('variant:change', this.sync);
    this.root.addEventListener('change', this.sync);
    this.querySelector('[data-sticky-add]')?.addEventListener('click', (event) => {
      // The flight to the bag starts here when the photo is far up the page.
      this.root.pressed = event.currentTarget;
      this.root.querySelector('product-form form')?.requestSubmit();
    });
    // The real button's message is off screen while this bar shows: say it here too.
    this.root.addEventListener('buy:error', (event) => this.say(event.detail.message));
    this.sync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.pageObserver?.disconnect();
    document.removeEventListener('dialog:open', this.update);
    document.removeEventListener('close', this.update, true);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.root?.removeEventListener('variant:change', this.sync);
    this.root?.removeEventListener('change', this.sync);
  }

  /** True while the bar, if shown, would lie over the product's name and price. */
  get coversName() {
    if (!this.head || !this.offsetHeight) return false;
    const box = this.head.getBoundingClientRect();
    return box.bottom > window.innerHeight - this.offsetHeight && box.top < window.innerHeight;
  }

  update() {
    const show = this.realOffScreen && !this.footerOnScreen && !document.querySelector('dialog[open]') && !this.coversName;
    if (show === this.classList.contains('is-shown')) return;
    this.classList.toggle('is-shown', show);
    this.inert = !show;
  }

  /** Why the piece could not be added, in place of the size line, until he does something else. */
  say(message) {
    const line = this.querySelector('[data-sticky-size]');
    if (!line) return;
    if (!message) {
      if (line.classList.contains('is-error')) {
        line.classList.remove('is-error');
        this.sync();
      }
      return;
    }
    line.classList.add('is-error');
    line.textContent = message;
  }

  /**
   * The bar's button is the real button again: same words, same price, same state. The line
   * beside it names the size once one is chosen.
   */
  sync() {
    const form = this.root.querySelector('product-form');
    const real = form?.querySelector('button[type="submit"]');
    if (!real) return;
    // The real button first: both listen for the same change, and this one may hear it first.
    form.sync?.();
    const sizes = this.root.querySelector('variant-picker [data-size-option]');
    const chosen = sizes?.querySelector('input:checked');
    const line = this.querySelector('[data-sticky-size]');
    line?.classList.remove('is-error');
    if (line) line.textContent = chosen ? `${sizes.querySelector('.picker__name')?.textContent.trim()}: ${chosen.value}` : '';
    this.mirror();
  }

  /** The bar's button takes the real button's words, price and state ("Adding…" too). */
  mirror() {
    const real = this.root.querySelector('product-form button[type="submit"]');
    if (!real) return;
    const price = real.querySelector('.price')?.textContent;
    if (price) for (const mine of this.querySelectorAll('[data-sticky-price]')) mine.textContent = price;
    const button = this.querySelector('[data-sticky-add]');
    button.disabled = real.disabled;
    for (const state of ['aria-disabled', 'aria-busy']) {
      if (real.getAttribute(state) === 'true') button.setAttribute(state, 'true');
      else button.removeAttribute(state);
    }
    const label = button.querySelector('[data-sticky-label]');
    if (label) label.textContent = real.querySelector('[data-label]')?.textContent.trim() || '';
  }
}

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.slides = [...this.track.children];
    // Which group of photos each variant shows, worked out in sections/main-product.liquid.
    // Missing when the product's photos are not grouped by colour.
    try {
      this.groups = this.dataset.groups ? JSON.parse(this.dataset.groups) : null;
    } catch {
      this.groups = null;
    }
    this.current = this.querySelector('[data-current]');
    this.total = this.querySelector('[data-total]');
    this.counter = this.querySelector('[data-counter]');
    this.progress = this.querySelector('[data-progress]');
    this.previous = this.querySelector('[data-previous]');
    this.next = this.querySelector('[data-next]');
    this.viewer = this.querySelector('product-viewer');

    this.previous?.addEventListener('click', () => this.show(this.index - 1));
    this.next?.addEventListener('click', () => this.show(this.index + 1));
    this.track.addEventListener('scroll', () => window.requestAnimationFrame(() => this.update()), { passive: true });
    this.track.addEventListener('click', (event) => this.zoom(event));
    // The full-screen viewer moved to another photo: the page behind it follows, so that
    // closing lands on the photo he was looking at.
    this.addEventListener('viewer:change', (event) => {
      const at = this.visible.indexOf(event.detail?.slide);
      if (at >= 0 && at !== this.index) this.show(at, 'instant');
    });

    this.root = this.closest('[data-product-root]');
    this.onVariantChange = (event) => this.showVariant(event.detail || {});
    this.root?.addEventListener('variant:change', this.onVariantChange);
    this.update();
  }

  disconnectedCallback() {
    this.root?.removeEventListener('variant:change', this.onVariantChange);
  }

  get index() {
    const width = this.track.clientWidth || 1;
    return Math.round(Math.abs(this.track.scrollLeft) / width);
  }

  /** The photo on show right now. */
  get photo() {
    const visible = this.visible;
    return visible[Math.min(this.index, visible.length - 1)]?.querySelector('img') || null;
  }

  /** The slides on show: all of them, or only the chosen colour's. */
  get visible() {
    return this.slides.filter((slide) => !slide.hidden);
  }

  show(index, behavior = 'smooth') {
    const visible = this.visible;
    const slide = visible[Math.max(0, Math.min(index, visible.length - 1))];
    if (!slide) return;
    const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    this.track.scrollTo({ left: direction * visible.indexOf(slide) * this.track.clientWidth, behavior });
  }

  /** A photo was tapped or clicked: it opens full screen, with the photos of the colour on show. */
  zoom(event) {
    const opener = event.target instanceof Element ? event.target.closest('[data-zoom]') : null;
    if (!opener || !this.viewer?.open) return;
    const items = this.visible
      .map((slide) => ({ slide, opener: slide.querySelector('[data-zoom]'), template: slide.querySelector('template[data-viewer-media]'), thumb: slide.querySelector('img') }))
      .filter((item) => item.opener || item.template);
    this.viewer.open(items, Math.max(0, items.findIndex((item) => item.opener === opener)));
  }

  /**
   * Puts a copy of the photos that show now on top of the gallery, so the change of colour can
   * cross-fade: the new photos are swapped in underneath and the copy fades away.
   * @returns {Promise<HTMLElement|null>}
   */
  async ghost() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !this.track.animate) return null;
    const copy = this.track.cloneNode(true);
    copy.className = 'product__track product__track--ghost';
    copy.removeAttribute('data-track');
    copy.removeAttribute('aria-label');
    copy.setAttribute('aria-hidden', 'true');
    copy.inert = true;
    const photos = [...copy.querySelectorAll('.product__slide:not([hidden]) img')];
    for (const photo of photos) {
      photo.loading = 'eager';
      photo.removeAttribute('fetchpriority');
    }
    this.append(copy);
    copy.scrollLeft = this.track.scrollLeft;
    // The copies come from the browser's memory; a moment is allowed for them to be drawn.
    await Promise.race([Promise.all(photos.slice(0, 6).map((photo) => photo.decode?.().catch(() => {}))), new Promise((resolve) => window.setTimeout(resolve, 120))]);
    return copy;
  }

  /**
   * After a variant change: swaps to that colour's photos when photos are grouped (a 160 ms
   * cross-fade), then moves to the variant's own photo.
   * @param {{variantId?: string, featuredMediaId?: string}} detail
   */
  async showVariant({ variantId, featuredMediaId }) {
    const turn = (this.turn = (this.turn || 0) + 1);
    // 0 means the colour has no photos of its own, so every photo shows.
    const group = this.groups ? Number(this.groups[variantId] || 0) : 0;
    const hides = (slide) => {
      const slideGroup = Number(slide.dataset.group || 0);
      return group > 0 && slideGroup > 0 && slideGroup !== group;
    };
    const swapped = Boolean(this.groups) && this.slides.some((slide) => slide.hidden !== hides(slide));
    let copy = null;
    if (swapped) {
      this.querySelector('.product__track--ghost')?.remove();
      copy = await this.ghost();
      if (turn !== this.turn) {
        copy?.remove();
        return;
      }
      for (const slide of this.slides) slide.hidden = hides(slide);
    }
    const visible = this.visible;
    this.slides.forEach((slide) => slide.classList.toggle('is-lead', slide === visible[0]));
    const index = featuredMediaId ? visible.findIndex((slide) => slide.dataset.mediaId === String(featuredMediaId)) : -1;
    if (index >= 0) this.show(index, swapped ? 'instant' : 'smooth');
    else if (swapped) this.show(0, 'instant');
    this.update();
    copy?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, easing: 'ease' }).finished.finally(() => copy.remove());
  }

  update() {
    const index = this.index;
    const visible = this.visible;
    const count = visible.length;
    const shown = Math.min(index, Math.max(count - 1, 0));
    if (this.current) this.current.textContent = Kanze.digits(shown + 1);
    if (this.total) this.total.textContent = Kanze.digits(count);
    if (this.previous) this.previous.disabled = index <= 0;
    if (this.next) this.next.disabled = index >= count - 1;

    // Up to eight photos: one thin line each. More: "3 / 13". Both keep their place when they
    // are not shown, so a colour with more or fewer photos moves nothing.
    if (this.counter) this.counter.hidden = count <= 8;
    if (this.progress) {
      const lines = count > 1 && count <= 8;
      this.progress.hidden = !lines;
      if (lines) {
        while (this.progress.children.length > count) this.progress.lastElementChild.remove();
        while (this.progress.children.length < count) this.progress.append(Object.assign(document.createElement('span'), { className: 'product__segment' }));
        [...this.progress.children].forEach((line, at) => line.classList.toggle('is-active', at === shown));
      }
    }

    // "View photo 2 full screen": the number is the photo's place among the photos on show.
    const first = visible[0]?.dataset.mediaId || '';
    if (this.dataset.zoomLabel && `${first}/${count}` !== this.labelled) {
      this.labelled = `${first}/${count}`;
      visible.forEach((slide, at) => slide.querySelector('[data-zoom]')?.setAttribute('aria-label', this.dataset.zoomLabel.replace('[number]', Kanze.digits(at + 1))));
    }
  }
}

/**
 * The details column of the product page. On a wide screen it stays in view beside the photos.
 * A column shorter than the screen rests under the header. A taller one moves with the page
 * until its end is on screen, rests there, and comes back down as he scrolls up: every part of
 * it can always be reached, and opening a row in it moves nothing above that row.
 *
 * CSS does the resting (position: sticky). This only says where: --details-top, between the
 * place under the header (the element's scroll-margin) and the place where its end shows.
 */
class ProductDetails extends HTMLElement {
  connectedCallback() {
    this.wide = window.matchMedia('(min-width: 1024px)');
    this.last = window.scrollY;
    this.at = null;
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  onScroll() {
    const moved = window.scrollY - this.last;
    this.last = window.scrollY;
    if (!this.wide.matches || !this.closest('.product--sticky')) {
      if (this.at !== null) this.style.removeProperty('--details-top');
      this.at = null;
      return;
    }
    const rest = parseFloat(getComputedStyle(this).scrollMarginTop) || 0;
    const low = Math.min(rest, window.innerHeight - this.offsetHeight - rest);
    let at = this.at ?? rest;
    // Down: towards the place where the end shows, never past it. A column that grew stays put.
    if (moved > 0 && at > low) at = Math.max(low, at - moved);
    else if (moved < 0) at = at - moved;
    at = Math.min(at, rest);
    if (at === this.at) return;
    this.at = at;
    this.style.setProperty('--details-top', `${at}px`);
  }
}

class KanzeRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.previous = this.querySelector('[data-previous]');
    this.next = this.querySelector('[data-next]');
    this.previous?.addEventListener('click', () => this.move(-1));
    this.next?.addEventListener('click', () => this.move(1));
    this.track.addEventListener('scroll', () => window.requestAnimationFrame(() => this.update()), { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);
    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  move(direction) {
    const reading = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    this.track.scrollBy({ left: direction * reading * this.track.clientWidth * 0.8, behavior: 'smooth' });
  }

  update() {
    const scrolled = Math.abs(this.track.scrollLeft);
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (this.previous) this.previous.hidden = max <= 4 || scrolled <= 4;
    if (this.next) this.next.hidden = max <= 4 || scrolled >= max - 4;
  }
}

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded || !this.dataset.url) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    this.dataset.loaded = 'true';
    try {
      const response = await fetch(this.dataset.url);
      if (!response.ok) return;
      const fresh = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('product-recommendations');
      if (fresh && fresh.innerHTML.trim() !== '') this.innerHTML = fresh.innerHTML;
    } catch (error) {
      console.error('Kanze: could not load recommendations.', error);
    }
  }
}

/**
 * Opens the size sheet for a product and fills it with that product's picker.
 * Falls back to visiting the product page if the sheet cannot be loaded.
 * @param {string} productUrl - The product URL, optionally with ?variant=
 * @param {HTMLElement} [opener] - The control that asked for it; gets focus back afterwards
 * @param {{swap?: {key: string, variant: number, quantity: number}}} [how] - `swap`: the sheet was opened by
 *   "Change size" in the bag. His size shows as chosen, and the button replaces that line, keeping the quantity.
 */
Kanze.quickAdd = async (productUrl, opener, { swap = null } = {}) => {
  const dialog = document.getElementById('QuickAdd');
  const content = dialog?.querySelector('[data-quick-add-content]');
  if (!dialog || !content) {
    window.location.assign(productUrl);
    return;
  }

  // The sheet opens once it is whole: its sizes are in and, when "Find my size" knows his,
  // that size is marked. It then slides in at its final height and nothing in it moves after.
  // Until then the control he pressed shows it is working.
  const turn = (Kanze.quickAddTurn = (Kanze.quickAddTurn || 0) + 1);
  opener?.setAttribute('aria-busy', 'true');
  try {
    const fresh = document.importNode(await loadSheet(productUrl), true);
    if (turn !== Kanze.quickAddTurn) return;
    if (swap) Object.assign(fresh.dataset, { swapKey: swap.key, swapVariant: swap.variant, swapQuantity: swap.quantity, sizeChosen: 'true' });
    content.replaceChildren(fresh);
    if (swap) {
      // The size he has is his own choice: it shows as chosen. The sheet was asked for with
      // that size, so nothing has to be fetched again.
      const mine = fresh.querySelector('variant-picker [data-size-option] input[data-selected]:not(:disabled)');
      if (mine) mine.checked = true;
      fresh.querySelector('product-form')?.sync?.();
    } else await suggestSize(content);
    if (turn !== Kanze.quickAddTurn) return;
    Kanze.quickAddOpener = opener || null;
    Kanze.openDialog(dialog, opener);
  } catch (error) {
    console.error('Kanze: could not open the size sheet.', error);
    window.location.assign(productUrl);
  } finally {
    opener?.removeAttribute('aria-busy');
  }
};

/**
 * Asks Shopify for a product's size sheet. The answer is kept for a few seconds, so the request
 * can start the moment a finger lands on the + and be there when the tap is done.
 * @param {string} productUrl
 * @returns {Promise<Element>}
 */
const sheets = new Map();
function loadSheet(productUrl) {
  if (!sheets.has(productUrl)) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', 'quick-add');
    const request = fetch(url).then(async (response) => {
      if (!response.ok) throw new Error(`Quick add request failed (${response.status})`);
      const sheet = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('.quick-add');
      if (!sheet) throw new Error('Quick add content missing');
      return sheet;
    });
    sheets.set(productUrl, request);
    // Stock changes: an answer is good for ten seconds, and a failed one is not kept at all.
    request.catch(() => sheets.delete(productUrl));
    window.setTimeout(() => sheets.delete(productUrl), 10000);
  }
  return sheets.get(productUrl);
}
document.addEventListener('pointerdown', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-quick-add]') : null;
  if (trigger?.dataset.quickAdd) loadSheet(trigger.dataset.quickAdd).catch(() => {});
}, { capture: true, passive: true });
document.addEventListener('cart:updated', () => sheets.clear());

/**
 * In the size sheet: when he has answered "Find my size" and this product has a ready Fit
 * Table, his size is chosen for him and marked "Your size". He still presses Add to bag
 * himself, and he can tap another size. Nothing happens for a size that is sold out.
 * Done when the sheet is ready to be shown.
 * @param {Element} sheet - The sheet's content
 */
async function suggestSize(sheet) {
  const source = sheet.querySelector('[data-fit-table]');
  if (!source) return;
  try {
    const { recommendFromSaved, norm } = await import(source.dataset.module);
    const result = recommendFromSaved(JSON.parse(source.textContent));
    if (!result?.ok || !source.isConnected) return;
    const input = [...sheet.querySelectorAll('variant-picker [data-size-option] input[type="radio"]')].find((radio) => norm(radio.value) === norm(result.size));
    if (!input || input.disabled || sheet.querySelector('variant-picker [data-size-option] input:checked')) return;
    const root = sheet.querySelector('[data-quick-add-root]');
    // The picker is drawn again after every choice, so the mark is put back each time.
    const mark = () => {
      const chip = [...sheet.querySelectorAll('variant-picker [data-size-option] input[type="radio"]')].find((radio) => norm(radio.value) === norm(result.size))?.nextElementSibling;
      chip?.classList.add('is-yours');
      chip?.setAttribute('data-yours', root?.dataset.yourSizeLabel || '');
    };
    root?.setAttribute('data-your-size', result.size);
    root?.addEventListener('variant:change', mark);
    input.checked = true;
    input.dataset.by = 'find_my_size';
    // The picker is drawn again for that size. The sheet waits for it, but not for long.
    const drawn = new Promise((resolve) => {
      root?.addEventListener('variant:change', resolve, { once: true });
      window.setTimeout(resolve, 1500);
    });
    input.dispatchEvent(new Event('change', { bubbles: true }));
    mark();
    await drawn;
  } catch (error) {
    console.error('Kanze: could not work out the size for the sheet.', error);
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const trigger = target?.closest('[data-quick-add]');
  if (trigger) {
    event.preventDefault();
    Kanze.quickAdd(trigger.dataset.quickAdd, trigger);
    return;
  }

  // A colour dot on a card: show that colour's photo and make the card lead to that colour.
  const dot = target?.closest('.card [data-color-dot]');
  if (!dot) return;
  const card = dot.closest('.card');
  for (const other of card.querySelectorAll('[data-color-dot]')) other.setAttribute('aria-pressed', String(other === dot));
  const photo = card.querySelector('.card__image:not(.card__image--hover)');
  if (photo && dot.dataset.image) {
    const address = new URL(dot.dataset.image, window.location.href);
    const sized = (width) => {
      address.searchParams.set('width', width);
      return address.href;
    };
    photo.srcset = [200, 300, 400, 500, 600, 800, 1000].map((width) => `${sized(width)} ${width}w`).join(', ');
    photo.src = sized(600);
    // The second photo belongs to the first colour, so it no longer shows on hover. On a
    // phone the swipeable photos go back to the start and only this colour's photo stays.
    card.classList.add('card--colour-chosen');
    card.querySelector('.card__media-link')?.scrollTo({ left: 0, behavior: 'instant' });
  }
  // Another colour that is a product of its own has its own price.
  const price = card.querySelector('.card__price');
  if (price && dot.dataset.price) {
    price.own ??= price.innerHTML;
    price.innerHTML = `${dot.dataset.price} `;
    if (dot.dataset.saving) price.append(Object.assign(document.createElement('span'), { className: 'card__saving text-sale', textContent: dot.dataset.saving }));
  } else if (price?.own) {
    // Back to one of the card's own colours: its own price.
    price.innerHTML = price.own;
  }
  // A colour that is sold out shows as a sold-out card does: its photo dimmed, no "+".
  card.classList.toggle('card--colour-gone', dot.dataset.gone === 'true');
  if (dot.dataset.url) {
    for (const link of card.querySelectorAll('a.card__media-link, a.card__link')) link.href = dot.dataset.url;
    const quickAdd = card.querySelector('[data-quick-add]');
    if (quickAdd) quickAdd.dataset.quickAdd = dot.dataset.url;
  }
});

/* ---------- Phones: swipeable photos on a card in a grid (snippets/product-card.liquid) ----------
 * The swipe itself is the browser's own sideways scroll with snap. This only keeps the dots in
 * step, and fetches the other photos the moment a finger lands on the card, so the next one is
 * there when he swipes. Scroll events do not bubble, so they are caught on the way down.
 */
document.addEventListener(
  'scroll',
  (event) => {
    const photos = event.target instanceof Element && event.target.matches('.card--swipe .card__media-link') ? event.target : null;
    if (!photos || photos.dotsFrame) return;
    photos.dotsFrame = window.requestAnimationFrame(() => {
      photos.dotsFrame = 0;
      const at = Math.round(Math.abs(photos.scrollLeft) / photos.clientWidth);
      photos.parentElement.querySelectorAll('.card__photo-dot').forEach((dot, index) => dot.classList.toggle('is-active', index === at));
    });
  },
  { capture: true, passive: true }
);
document.addEventListener(
  'touchstart',
  (event) => {
    const photos = event.target instanceof Element ? event.target.closest('.card--swipe .card__media-link') : null;
    if (!photos || photos.dataset.ready) return;
    photos.dataset.ready = 'true';
    for (const image of photos.querySelectorAll('img[loading="lazy"]')) image.loading = 'eager';
  },
  { passive: true }
);

/* ---------- "?size=L" in the address ----------
 * A link may ask for a size by its name, when it cannot know the variant: the size he had
 * chosen on another colour of the piece, for one. It counts as his own choice, and is taken
 * only when the product has that size and has it in stock. (The theme's own links between
 * colours name the variant itself, so the page arrives with the size already chosen.)
 */
const wantedSize = new URLSearchParams(window.location.search).get('size');
if (wantedSize) {
  window.customElements.whenDefined('variant-picker').then(() => {
    const same = (text) => String(text).trim().toLowerCase() === wantedSize.trim().toLowerCase();
    const size = [...document.querySelectorAll('[data-product-root] variant-picker[data-update-url] [data-size-option] input[type="radio"]')].find((radio) => same(radio.value));
    if (!size || size.disabled || size.checked) return;
    size.checked = true;
    size.dataset.by = 'url';
    size.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

for (const [name, constructor] of [
  ['variant-picker', VariantPicker],
  ['product-form', ProductForm],
  ['sticky-buy', StickyBuy],
  ['product-gallery', ProductGallery],
  ['product-details', ProductDetails],
  ['kanze-rail', KanzeRail],
  ['product-recommendations', ProductRecommendations],
]) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
